import React, {
  Dispatch,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useCallback,
  useState,
} from 'react';
import ReactDOM from 'react-dom/client';
import ReactModal from 'react-modal';
import {
  defaultSizesByFieldType,
  fieldShapes,
} from 'Components/Interact/common/fieldShapes';
import UIButton from 'Components/UIComponents/UIButton';
import { DeleteDocumentField } from 'Hooks/DocumentFields/useDocumentFieldDelete';
import { UpdateDocumentField } from 'Hooks/DocumentFields/useDocumentFieldUpdate';
import { SelectableOption } from 'Interfaces/Common';
import { DocumentTypes, DocumentValues } from 'Interfaces/Document';
import { DocumentField, DocumentFieldTypes } from 'Interfaces/DocumentFields';
import { checkIfDateOrText } from 'Utils/functions';

import FieldBarItem from './FieldBarItem';
import FieldItemOptions from './FieldItemOptions';
import FieldItemView from './FieldItemView';
import InteractCloseButton from './InteractCloseButton';
import InteractSelectItemTitle from './InteractSelectItemTitle';
import SelectActionForm from './SelectActionForm';

interface InteractMobileFieldBarProps {
  wizardFormStep: number;
  fieldRootRef: MutableRefObject<ReactDOM.Root | null>;
  containerRef: RefObject<HTMLDivElement>;
  documentInitialValues?: DocumentValues;
  documentScale?: number;
  selectedScale: number;
  participantOptions?: SelectableOption<string>[];
  selectedFieldType: DocumentFieldTypes | undefined;
  setSelectedFieldType: Dispatch<SetStateAction<DocumentFieldTypes | undefined>>;
  setCurrentDocumentType: (type: DocumentTypes) => void;
  setFieldShapeExist: Dispatch<SetStateAction<boolean>>;
  setLastInteractedFieldId?: Dispatch<SetStateAction<string | null>>;
  isDocumentFirstAndDisabled: boolean;
  isInteractPreparing: boolean;
  lastInteractedFieldId: string | null;
  field?: DocumentField;
  fieldShapeContainerRef: MutableRefObject<HTMLDivElement | null>;
  isLoading?: boolean;
  isChooseActionDisabled?: boolean;
  onClose: () => void;
  onSubmit: (...args: unknown[]) => void;
  onChooseStep?: (step: number) => void;
  onSignerChange: (signerId: string) => void;
  onApplyFontToEachField: (fontFamily: string, fontSize: number) => void;
  onFieldUpdate: UpdateDocumentField;
  onFieldDelete: DeleteDocumentField;
  onStartInputTyping: () => void;
  onStopInputTyping: () => void;
  onFieldShapeRemove: () => void;
  validateTag: (id: string, tag: string) => boolean;
}

const InteractMobileFieldBar = ({
  containerRef,
  documentScale = 1,
  field,
  fieldRootRef,
  fieldShapeContainerRef,
  isDocumentFirstAndDisabled,
  isInteractPreparing,
  lastInteractedFieldId,
  documentInitialValues,
  isLoading,
  isChooseActionDisabled,
  participantOptions = [],
  onClose,
  onSubmit,
  onChooseStep,
  onFieldUpdate,
  onSignerChange,
  onFieldDelete,
  onFieldShapeRemove,
  onStartInputTyping,
  onStopInputTyping,
  onApplyFontToEachField,
  selectedScale,
  selectedFieldType,
  setCurrentDocumentType,
  setFieldShapeExist,
  setSelectedFieldType,
  setLastInteractedFieldId,
  validateTag,
  wizardFormStep,
}: InteractMobileFieldBarProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const deleteFieldShape = useCallback(() => {
    setFieldShapeExist(false);
    setSelectedFieldType(undefined);
    onFieldShapeRemove();
  }, [onFieldShapeRemove, setFieldShapeExist, setSelectedFieldType]);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
    deleteFieldShape();
    onClose();
  }, [onClose, deleteFieldShape]);

  const handleCancel = () => {
    if (wizardFormStep !== 2) return;
    deleteFieldShape();
    if (!!lastInteractedFieldId && setLastInteractedFieldId) {
      setLastInteractedFieldId(null);
    } else if (!lastInteractedFieldId && onChooseStep) {
      onChooseStep(2);
    }
  };

  const handleFieldBarItemCursorMove = useCallback(
    (event, fieldType: DocumentFieldTypes) => {
      if ('pointerType' in event && event.pointerType === 'touch') {
        return;
      }

      if (
        !containerRef.current ||
        !event.interaction.pointerIsDown ||
        event.interaction.interacting()
      ) {
        return;
      }
      const { clientX: pointerX, clientY: pointerY } = event;
      const { width, height } = defaultSizesByFieldType[fieldType];
      const draggable = document.createElement('div');
      containerRef.current.appendChild(draggable);
      const isDateOrText = checkIfDateOrText(fieldType);

      ReactDOM.createRoot(draggable).render(
        <FieldItemView
          data-clone="true"
          fieldType={fieldType}
          className={fieldType}
          style={{
            userSelect: 'none',
            position: 'fixed',
            left: pointerX - (width * documentScale) / 2,
            top: pointerY - (height * documentScale) / 2,
            [isDateOrText ? 'minWidth' : 'width']: width,
            [isDateOrText ? 'minHeight' : 'height']: height,
            transform: `scale(${documentScale})`,
            transformOrigin: 'top left',
          }}
          selectedScale={0.9 * documentScale}
        />,
      );

      draggable.firstChild?.dispatchEvent(
        new PointerEvent('pointerdown', {
          ...event,
          bubbles: true,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentScale],
  );

  const handleCreateFieldShape = useCallback(
    (
      event: ReactMouseEvent<HTMLLIElement>,
      type: DocumentFieldTypes,
      renderShape?: boolean,
    ) => {
      event.stopPropagation();

      if (fieldRootRef.current && fieldShapeContainerRef.current && !renderShape) {
        return fieldRootRef.current.unmount();
      }

      onFieldShapeRemove();

      if (fieldShapeContainerRef.current && renderShape && type !== selectedFieldType) {
        fieldShapeContainerRef.current.style.top = `${event.clientY}px`;
        fieldShapeContainerRef.current.style.left = `${event.clientX}px`;

        setSelectedFieldType(type);
        setFieldShapeExist(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentScale, onFieldShapeRemove, selectedFieldType, selectedScale],
  );

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      className="interactModal__mobilePanel ReactModal__Content"
      overlayClassName="interactModal__mobilePanel-overlay"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={true}
      shouldReturnFocusAfterClose={false}
      shouldFocusAfterRender={false}
    >
      <div className="interactModal__mobilePanel-header">
        {!!lastInteractedFieldId && !!field && wizardFormStep > 1 ? (
          <InteractSelectItemTitle
            fieldType={field.type}
            showRemoveFieldButton
            onRemoveField={() => onFieldDelete({ id: field.id })}
          />
        ) : (
          <>
            <h3 className="interactModal__mobilePanel-title">
              {wizardFormStep === 1
                ? 'Choose an action'
                : wizardFormStep === 2
                ? 'Fields'
                : ''}
            </h3>
            <InteractCloseButton onClick={handleCloseModal} aria-label="Close" />
          </>
        )}
      </div>
      <div className="interactModal__mobilePanel-content">
        {!!lastInteractedFieldId && !!field && wizardFormStep > 1 ? (
          <ul className="interactModal__fieldBar-selectField-list">
            {field && (
              <FieldItemOptions
                field={field}
                partisipantOptions={participantOptions}
                showTitle={false}
                showCancelButton
                onClose={handleCancel}
                onUpdate={onFieldUpdate}
                onChangeSigner={onSignerChange}
                onDelete={onFieldDelete}
                onStartInputTyping={onStartInputTyping}
                onStopInputTyping={onStopInputTyping}
                onApplyFontToEachField={onApplyFontToEachField}
                validateTag={validateTag}
              />
            )}
          </ul>
        ) : wizardFormStep === 1 ? (
          <SelectActionForm
            initialValues={documentInitialValues}
            onSubmit={onSubmit}
            setCurrentDocumentType={setCurrentDocumentType}
            isLoading={isLoading}
            isChooseActionDisabled={isChooseActionDisabled}
            showFormTitle={false}
          />
        ) : (
          <div className="interactModal__fieldBar-fieldList">
            {fieldShapes.map(fieldShape => (
              <FieldBarItem
                key={fieldShape.type}
                selectedFieldType={selectedFieldType}
                handleSelectField={setSelectedFieldType}
                fieldShape={fieldShape}
                disabled={isInteractPreparing || isDocumentFirstAndDisabled}
                onClick={handleCreateFieldShape}
                onCursorMove={handleFieldBarItemCursorMove}
              />
            ))}
          </div>
        )}
      </div>

      {onChooseStep &&
        setLastInteractedFieldId &&
        wizardFormStep === 2 &&
        field &&
        !checkIfDateOrText(field.type) && (
          <div className="interactModal__header-send interactModal__mobilePanel-footer">
            <UIButton
              className="interactModal__header-send-button"
              priority="white"
              title="Cancel"
              handleClick={handleCancel}
            />
          </div>
        )}
    </ReactModal>
  );
};

export default InteractMobileFieldBar;
